import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/AuthProvider';

interface Rubric {
  id: string;
  name: string;
  grade_level: string;
  question_type: string;
  total_score: number;
  is_template: boolean;
  created_at: string;
}

const questionTypeLabels: Record<string, string> = {
  mixed: '混合題型',
  essay: '作文',
  short_answer: '短答題',
  reading_comprehension: '閱讀理解',
  fill_in_blank: '填充題',
  multiple_choice: '選擇題',
  dictation: '默書',
  true_false: '是非題',
};

export function RubricsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchRubrics = async () => {
      const { data, error } = await supabase
        .from('rubrics')
        .select('*')
        .or(`teacher_id.eq.${user.id},is_template.eq.true`)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setRubrics(data);
      }
      setLoading(false);
    };

    fetchRubrics();
  }, [user]);

  const handleDelete = async (id: string) => {
    if (!confirm('確定要刪除此評分準則嗎？')) return;

    await supabase.from('rubrics').delete().eq('id', id);
    setRubrics(rubrics.filter((r) => r.id !== id));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          評分準則
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/rubrics/new')}
        >
          新增準則
        </Button>
      </Box>

      {rubrics.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary" gutterBottom>
              尚未有自訂評分準則
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/rubrics/new')}
              sx={{ mt: 2 }}
            >
              建立第一個準則
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {rubrics.map((rubric) => (
            <Grid key={rubric.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {rubric.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Chip label={rubric.grade_level} size="small" />
                        <Chip
                          label={questionTypeLabels[rubric.question_type] || rubric.question_type}
                          size="small"
                          variant="outlined"
                        />
                        {rubric.is_template && (
                          <Chip label="範本" size="small" color="primary" />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        總分：{rubric.total_score} 分
                      </Typography>
                    </Box>
                    {!rubric.is_template && (
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/rubrics/${rubric.id}/edit`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(rubric.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

